import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import type { Asset, AssetAssignment, AssetGroup, AssetTarget, AssetTracker } from '../types/payload'
import { getHasManyRelationshipIds, getHasOneJoinId, getHasOneRelationshipId } from '../utils/payloadHelpers'

//
// SCHEMAS
//
export const assetSchema = z.object({
  name: z.string().min(3, 'Name must be unique and at least 3 characters long.'),
  identifier: z.string().min(3, 'Identifier must be unique and at least 3 characters long.'),
  visibilityStatus: z.enum(['active', 'inactive', 'archived'] as const satisfies Asset['visibilityStatus'][]),
  internalNotes: z.string(),
  memberNotes: z.string(),
  assetGroup: z.string({ message: 'Asset must belong to a group.' }),
  assetTrackers: z.array(z.string()),
})

export const assetGroupSchema = z.object({
  name: z.string().min(3, 'Name must be unique and at least 3 characters long.'),
  visibilityStatus: z.enum(['active', 'archived'] as const satisfies AssetGroup['visibilityStatus'][]),
  internalNotes: z.string(),
  defaultTarget: z.string().nullish(),
})

export const assetTrackerSchema = z.object({
  identifier: z
    .string()
    .regex(/^[a-zA-Z0-9\-\:]+$/, 'Identifier must not contain special characters.')
    .min(8, 'Identifier must be unique and at least 8 characters long.'),
  type: z.enum(['nfc', 'qr', 'other'] as const satisfies AssetTracker['type'][]),
  visibilityStatus: z.enum(['active', 'archived'] as const satisfies AssetTracker['visibilityStatus'][]),
  internalNotes: z.string(),
  asset: z.string().nullish(),
})

export const assetTargetSchema = z.object({
  name: z.string().min(3, 'Name must be unique and at least 3 characters long.'),
  type: z.enum(['team', 'location', 'other']),
  visibilityStatus: z.enum(['internal', 'public', 'archived'] as const satisfies AssetTarget['visibilityStatus'][]),
  internalNotes: z.string(),
  memberNotes: z.string(),
})

export const assetAssignmentSchema = z
  .object({
    internalNotes: z.string(),
    memberNotes: z.string(),
    creator: z.string(),
    assignees: z.array(z.string()),
    asset: z.string({ message: "Asset can't be blank." }),
    assetTarget: z.string().nullish(),
    assetTargetAsset: z.string().nullish(),
    assetTracker: z.string().nullish(),
  })
  .superRefine((data, ctx) => {
    if ((!!data.assetTarget && !!data.assetTargetAsset) || (!data.assetTarget && !data.assetTargetAsset)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assetTarget'],
        fatal: true,
        message: 'Must have either a Target or an Asset.',
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['assetTargetAsset'],
        fatal: true,
        message: 'Must have either a Target or an Asset.',
      })
    }
  })

//
// DEFAULTS
//
export function castAssetToForm(data?: Asset): z.infer<typeof assetSchema> {
  return {
    name: data?.name ?? '',
    identifier: data?.identifier ?? '',
    visibilityStatus: data?.visibilityStatus ?? 'active',
    internalNotes: data?.internalNotes ?? '',
    memberNotes: data?.memberNotes ?? '',
    assetGroup: getHasOneRelationshipId(data?.assetGroup, ''),
    assetTrackers: getHasManyRelationshipIds(data?.assetTrackers),
  }
}

export function castAssetGroupToForm(data?: AssetGroup): z.infer<typeof assetGroupSchema> {
  return {
    name: data?.name ?? '',
    visibilityStatus: data?.visibilityStatus ?? 'active',
    internalNotes: data?.internalNotes ?? '',
    defaultTarget: getHasOneRelationshipId(data?.defaultTarget),
  }
}

export function castAssetTrackerToForm(data?: AssetTracker): z.infer<typeof assetTrackerSchema> {
  return {
    identifier: data?.identifier ?? '',
    type: data?.type ?? 'nfc',
    visibilityStatus: data?.visibilityStatus ?? 'active',
    internalNotes: data?.internalNotes ?? '',
    asset: getHasOneJoinId(data?.asset),
  }
}

export function castAssetTargetToForm(data?: AssetTarget): z.infer<typeof assetTargetSchema> {
  return {
    name: data?.name ?? '',
    type: data?.type ?? 'team',
    visibilityStatus: data?.visibilityStatus ?? 'internal',
    internalNotes: data?.internalNotes ?? '',
    memberNotes: data?.memberNotes ?? '',
  }
}

export function castAssetAssignmentToForm(data?: Partial<AssetAssignment>): z.infer<typeof assetAssignmentSchema> {
  return {
    internalNotes: data?.internalNotes ?? '',
    memberNotes: data?.memberNotes ?? '',
    creator: getHasOneRelationshipId(data?.creator, ''),
    assignees: getHasManyRelationshipIds(data?.assignees),
    asset: getHasOneRelationshipId(data?.asset, ''),
    assetTarget: getHasOneRelationshipId(data?.assetTarget),
    assetTargetAsset: getHasOneRelationshipId(data?.assetTargetAsset),
    assetTracker: getHasOneRelationshipId(data?.assetTracker),
  }
}

//
// HOOKS
//
export function useAssetForm() {
  const form = useForm<z.infer<typeof assetSchema>>({
    mode: 'controlled',
    validate: zodResolver(assetSchema),
    validateInputOnChange: true,
    initialValues: castAssetToForm(),
  })

  return { form, schema: assetSchema }
}

export function useAssetGroupForm() {
  const form = useForm<z.infer<typeof assetGroupSchema>>({
    mode: 'controlled',
    validate: zodResolver(assetGroupSchema),
    validateInputOnChange: true,
    initialValues: castAssetGroupToForm(),
  })

  return { form, schema: assetGroupSchema }
}

export function useAssetTrackerForm() {
  const form = useForm<z.infer<typeof assetTrackerSchema>>({
    mode: 'controlled',
    validate: zodResolver(assetTrackerSchema),
    validateInputOnChange: true,
    initialValues: castAssetTrackerToForm(),
  })

  return { form, schema: assetTrackerSchema }
}

export function useAssetTargetForm() {
  const form = useForm<z.infer<typeof assetTargetSchema>>({
    mode: 'controlled',
    validate: zodResolver(assetTargetSchema),
    validateInputOnChange: true,
    initialValues: castAssetTargetToForm(),
  })

  return { form, schema: assetTargetSchema }
}

export function useAssetAssignmentForm(data?: Partial<AssetAssignment>) {
  const form = useForm<z.infer<typeof assetAssignmentSchema>>({
    mode: 'controlled',
    validate: zodResolver(assetAssignmentSchema),
    validateInputOnChange: true,
    initialValues: castAssetAssignmentToForm(data),
    onValuesChange(values) {
      console.info(values)
    },
  })

  return { form, schema: assetAssignmentSchema }
}

//
// TYPECHECKS
//
const _assetKeysTypecheck: keyof Asset = '' as keyof z.infer<typeof assetSchema>
const _assetGroupKeysTypecheck: keyof AssetGroup = '' as keyof z.infer<typeof assetGroupSchema>
const _assetTrackerKeysTypecheck: keyof AssetTracker = '' as keyof z.infer<typeof assetTrackerSchema>
const _assetTargetKeysTypecheck: keyof AssetTarget = '' as keyof z.infer<typeof assetTargetSchema>
const _assetAssignmentKeysTypecheck: keyof AssetAssignment = '' as keyof z.infer<typeof assetAssignmentSchema>

//
// EXPORTS
//
export type AssetSchema = typeof assetSchema
export type AssetGroupSchema = typeof assetGroupSchema
export type AssetTrackerSchema = typeof assetTrackerSchema
export type AssetTargetSchema = typeof assetTargetSchema
export type AssetAssignmentSchema = typeof assetAssignmentSchema
